import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CCard, CCardBody, CButton, CFormInput, CInputGroup,
  CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell,
  CSpinner, CAlert, CPagination, CPaginationItem, CRow, CCol, CBadge,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilPencil, cilSearch, cilX, cilPlus, cilShieldAlt, cilInfo } from '@coreui/icons';
import api from '../services/api';
import SortableHeader from '../components/SortableHeader';

const STATUS_COLORS = {
  'Baru': 'primary', 'Dalam Proses': 'warning', 'Selesai': 'success',
  'Dipulangkan': 'info', 'Menunggu Maklumbalas': 'secondary', 'Waiting to Pickup': 'dark',
};

const PRIORITY_COLORS = { 'Penting': 'danger', 'Sederhana': 'warning', 'Biasa': 'secondary' };

function PaginationBar({ page, totalPages, setPage }) {
  const [goTo, setGoTo] = useState('');
  const pages = [];
  if (totalPages <= 9) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    [1, 2, 3].forEach(p => pages.push(p));
    if (page > 5) pages.push({ ellipsis: true, key: 'left' });
    for (let i = Math.max(4, page - 1); i <= Math.min(totalPages - 3, page + 1); i++) pages.push(i);
    if (page < totalPages - 4) pages.push({ ellipsis: true, key: 'right' });
    [totalPages - 2, totalPages - 1, totalPages].forEach(p => pages.push(p));
  }
  const handleGoTo = (e) => {
    e.preventDefault();
    const n = parseInt(goTo, 10);
    if (n >= 1 && n <= totalPages) { setPage(n); setGoTo(''); }
  };
  return (
    <div className="d-flex align-items-center gap-3 flex-wrap mt-3">
      <CPagination className="mb-0">
        <CPaginationItem disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</CPaginationItem>
        {pages.map((p) =>
          p.ellipsis
            ? <CPaginationItem key={p.key} disabled>…</CPaginationItem>
            : <CPaginationItem key={p} active={p === page} onClick={() => setPage(p)}>{p}</CPaginationItem>
        )}
        <CPaginationItem disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</CPaginationItem>
      </CPagination>
      <div className="d-flex align-items-center gap-2">
        <small className="text-muted">Page {page} of {totalPages}</small>
        <span className="text-muted">·</span>
        <form onSubmit={handleGoTo} className="d-flex align-items-center gap-1">
          <span className="text-muted small">Go to</span>
          <input type="number" min={1} max={totalPages} value={goTo} onChange={e => setGoTo(e.target.value)}
            placeholder="#" style={{ width: 56 }} className="form-control form-control-sm text-center" />
          <button type="submit" className="btn btn-sm btn-outline-secondary px-2">→</button>
        </form>
      </div>
    </div>
  );
}

function WarrantyPreviewModal({ item, onClose, onEdit }) {
  if (!item) return null;
  const alat = Array.isArray(item.alat) ? item.alat : (() => { try { return JSON.parse(item.alat || '[]'); } catch { return []; } })();
  const perhubungan = Array.isArray(item.perhubungan) ? item.perhubungan : (() => { try { return JSON.parse(item.perhubungan || '[]'); } catch { return []; } })();
  return (
    <CModal visible onClose={onClose} alignment="center" size="lg">
      <CModalHeader>
        <CModalTitle>
          <CIcon icon={cilShieldAlt} className="me-2 text-success" />
          {item.jobcode}
          {item.customer_name && <span className="ms-2 text-muted fw-normal" style={{ fontSize: 15 }}>— {item.customer_name}</span>}
        </CModalTitle>
      </CModalHeader>
      <CModalBody>
        <CRow className="g-3 small">
          <CCol xs={6} md={4}>
            <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Status</div>
            <CBadge color={STATUS_COLORS[item.status] || 'secondary'}>{item.status}</CBadge>
          </CCol>
          <CCol xs={6} md={4}>
            <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Priority</div>
            <CBadge color={PRIORITY_COLORS[item.keutamaan] || 'secondary'}>{item.keutamaan || '—'}</CBadge>
          </CCol>
          <CCol xs={6} md={4}>
            <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Date</div>
            <div className="fw-medium">{item.date || '—'}</div>
          </CCol>
          <CCol xs={6} md={4}>
            <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Staff</div>
            <div className="fw-medium">{item.admin_id || '—'}</div>
          </CCol>
          <CCol xs={6} md={4}>
            <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Customer ID</div>
            <div className="fw-medium">{item.custid || '—'}</div>
          </CCol>
          <CCol xs={6} md={4}>
            <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Serial No.</div>
            <div className="fw-medium">{item.no_siri || '—'}</div>
          </CCol>
          <CCol xs={6} md={4}>
            <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Claim Location</div>
            <div className="fw-medium">{item.tempat_claim || '—'}</div>
          </CCol>
          <CCol xs={6} md={4}>
            <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Cost (RM)</div>
            <div className="fw-medium">{item.kos || '—'}</div>
          </CCol>
          <CCol xs={12} md={8}>
            <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Equipment / Description</div>
            <div className="fw-medium">{item.jenis || '—'}</div>
          </CCol>
          {alat.length > 0 && (
            <CCol xs={12} md={8}>
              <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Devices</div>
              <div className="d-flex flex-wrap gap-1 mt-1">
                {alat.map(a => <CBadge key={a} color="light" textColor="dark">{a}</CBadge>)}
              </div>
            </CCol>
          )}
          {item.peralatan_lain && (
            <CCol xs={12} md={4}>
              <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Other Equipment</div>
              <div className="fw-medium">{item.peralatan_lain}</div>
            </CCol>
          )}
          {perhubungan.length > 0 && (
            <CCol xs={12}>
              <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Contact Methods</div>
              <div className="d-flex flex-wrap gap-1 mt-1">
                {perhubungan.map(p => <CBadge key={p} color="info" textColor="dark">{p}</CBadge>)}
              </div>
            </CCol>
          )}
          {item.catatan && (
            <CCol xs={12}>
              <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Notes</div>
              <div className="fw-medium" style={{ whiteSpace: 'pre-wrap' }}>{item.catatan}</div>
            </CCol>
          )}
        </CRow>
      </CModalBody>
      <CModalFooter>
        <CButton color="success" size="sm" onClick={() => onEdit(item.jobcode)}>
          <CIcon icon={cilPencil} className="me-1" /> Edit
        </CButton>
        <CButton color="secondary" variant="outline" onClick={onClose}>Close</CButton>
      </CModalFooter>
    </CModal>
  );
}

export default function WarrantyPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewItem, setPreviewItem] = useState(null);

  const pageSize = 20;
  const totalPages = Math.ceil(total / pageSize);

  function handleSort(col) {
    if (col === sortBy) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
    setPage(1);
  }

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/warranty', { params: { page, search, sortBy, sortDir } });
      setItems(data.warranty);
      setTotal(data.total);
    } catch {
      setError('Failed to load warranty claims.');
    } finally {
      setLoading(false);
    }
  }, [page, search, sortBy, sortDir]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function handleSearch(e) {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  }

  return (
    <>
      <WarrantyPreviewModal
        item={previewItem}
        onClose={() => setPreviewItem(null)}
        onEdit={(jobcode) => { setPreviewItem(null); navigate(`/warranty/${jobcode}/edit`); }}
      />

      <CRow className="mb-3 align-items-center">
        <CCol>
          <h5 className="mb-0">Warranty Claims</h5>
        </CCol>
        <CCol xs="auto">
          <CButton color="success" size="sm" onClick={() => navigate('/warranty/new')}>
            <CIcon icon={cilPlus} className="me-1" /> New Claim
          </CButton>
        </CCol>
      </CRow>

      <CCard>
        <CCardBody>
          <form onSubmit={handleSearch} className="d-flex gap-2 mb-3">
            <CInputGroup>
              <CFormInput
                placeholder="Search by claim code…"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
              />
              <CButton type="submit" color="secondary">
                <CIcon icon={cilSearch} />
              </CButton>
              {search && (
                <CButton color="secondary" variant="outline" onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }}>
                  <CIcon icon={cilX} />
                </CButton>
              )}
            </CInputGroup>
          </form>

          {error && <CAlert color="danger">{error}</CAlert>}

          {loading ? (
            <div className="text-center py-4"><CSpinner color="success" /></div>
          ) : (
            <>
              <div className="mb-2">
                <small className="text-muted d-flex align-items-center gap-1">
                  <CIcon icon={cilShieldAlt} size="sm" className="text-success" />
                  {search
                    ? <><strong>{total}</strong> result{total !== 1 ? 's' : ''} for "<em>{search}</em>"</>
                    : <><strong>{total}</strong> claim{total !== 1 ? 's' : ''}</>}
                </small>
              </div>

              <CTable hover responsive className="mb-0">
                <CTableHead color="light">
                  <CTableRow>
                    <SortableHeader col="jobcode"   label="Claim Code" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <CTableHeaderCell>Customer</CTableHeaderCell>
                    <SortableHeader col="status"    label="Status"     sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <SortableHeader col="keutamaan" label="Priority"   sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <SortableHeader col="date"      label="Date"       sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <CTableHeaderCell style={{ width: 60 }}></CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {items.map(w => (
                    <CTableRow key={w.jid} style={{ cursor: 'pointer' }} onClick={() => navigate(`/warranty/${w.jobcode}/edit`)}>
                      <CTableDataCell>{w.jobcode}</CTableDataCell>
                      <CTableDataCell>
                        <div className="d-flex align-items-center gap-1">
                          <span>{w.customer_name || <span className="text-muted">—</span>}</span>
                          <CButton
                            size="sm" color="secondary" variant="ghost"
                            style={{ padding: '0 4px', lineHeight: 1 }}
                            onClick={e => { e.stopPropagation(); setPreviewItem(w); }}
                            title="Preview claim"
                          >
                            <CIcon icon={cilInfo} size="sm" />
                          </CButton>
                        </div>
                      </CTableDataCell>
                      <CTableDataCell>
                        <CBadge color={STATUS_COLORS[w.status] || 'secondary'}>{w.status}</CBadge>
                      </CTableDataCell>
                      <CTableDataCell>{w.keutamaan || <span className="text-muted">—</span>}</CTableDataCell>
                      <CTableDataCell>{w.date}</CTableDataCell>
                      <CTableDataCell onClick={e => e.stopPropagation()}>
                        <CButton size="sm" color="success" variant="ghost" onClick={() => navigate(`/warranty/${w.jobcode}/edit`)} title="Edit">
                          <CIcon icon={cilPencil} />
                        </CButton>
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                  {items.length === 0 && (
                    <CTableRow>
                      <CTableDataCell colSpan={6} className="text-center text-muted py-4">No warranty claims found.</CTableDataCell>
                    </CTableRow>
                  )}
                </CTableBody>
              </CTable>

              {totalPages > 1 && <PaginationBar page={page} totalPages={totalPages} setPage={setPage} />}
            </>
          )}
        </CCardBody>
      </CCard>
    </>
  );
}
